import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {BulkArchiveFilesOptions} from 'archive/ArchiveActions';
import {getArchiveAction, getUnarchiveAction} from 'archive/ArchiveActionsUtil';
import {ArchivableItem, NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import BenchlingConfig from 'BenchlingConfig';
import {setWindowTitle, pluralize} from 'BenchlingUtil';
import {getOwnerChoices} from 'create-components/OwnableUtil';
import {LimitOffsetInput} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {TEMPLATES_SEEN_FEATURE_BANNER_PREFERENCE} from 'notebook/entry-templates/TemplateConstants';
import notifications from 'notifications';
import {FONT_PROXIMA_11, FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, NEAR_BLACK} from 'ui-library/Colors';
import EditableItemTable, {RowProps} from 'ui-library/EditableItemTable';
import FeatureBanner, {FeatureBannerItem} from 'ui-library/FeatureBanner';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import PlainSearchInput, {BorderRadius} from 'ui-library/PlainSearchInput';
import ResultsPager from 'ui-library/ResultsPager';
import runModal from 'ui-library/runModal';
import SettingsPage from 'ui-library/SettingsPage';
import {readableTime} from 'util/DateUtil';
import sortedBy from 'util/sortedBy';

import {
  TemplateCollectionsPageQuery,
  TemplateCollectionsPageQueryVariables,
} from './__generated/TemplateCollectionsPageQuery';
import ContextualTemplateEditingInlineBanner from './ContextualTemplateEditingInlineBanner';
import CreateTemplateCollectionModal from './CreateTemplateCollectionModal';
import {TYPE_TO_LABEL} from './TemplateCollectionPage';

type TemplateCollection = TemplateCollectionsPageQuery['templateCollectionListing']['collections'][number];

const COLUMN_WIDTH = 3;
const COLUMN_HEADERS = [
  {name: 'Name', width: COLUMN_WIDTH},
  {name: 'Owner', width: COLUMN_WIDTH},
  {name: 'Created', width: 2},
  {name: 'Last Modified', width: 2},
  {name: '# of templates', width: 2},
];
const EMPTY_SEARCH_QUERY = '';
export const DEFAULT_LIMIT_OFFSET_INPUT = {
  limit: 200,
  offset: 0,
};

function TemplateBanner(): JSX.Element {
  return (
    <FeatureBanner seenBannerPreference={TEMPLATES_SEEN_FEATURE_BANNER_PREFERENCE}>
      <div className="u-vertical-flex u-justify-content-start">
        <FeatureBannerItem
          title="Templates for notebook entries"
          subtitle="Save time with reusable entries"
          oversized={true}
        />
        <div className="u-flex-display u-spacing-top u-small-spacing-bottom">
          <FeatureBannerItem
            title="Make a template collection"
            subtitle="Use template collections to share your templates with collaborators"
            stepNumber={1}
            oversized={false}
          />
          <FeatureBannerItem
            title="Create a template"
            subtitle="Add content that you want to reuse across multiple entries."
            stepNumber={2}
            oversized={false}
          />
          <FeatureBannerItem
            title="Create an entry"
            subtitle="Get started by clicking 'Create entry from template' in your project folder."
            stepNumber={3}
            oversized={false}
          />
        </div>
      </div>
    </FeatureBanner>
  );
}

export const TEMPLATE_COLLECTIONS_PAGE_QUERY = gql`
  query TemplateCollectionsPageQuery(
    $limitOffsetInput: LimitOffsetInput
    $searchInput: StrictString
    $archivePurposes: [StrictString]
  ) @skipAudit @engComponent(name: NOTEBOOK) {
    templateCollectionListing(
      limitOffsetInput: $limitOffsetInput
      searchInput: $searchInput
      archivePurposes: $archivePurposes
    ) {
      collections {
        id
        name
        archiveRecord {
          purpose
        }
        ownerJson
        url
        createdAt
        modifiedAt
        permissions {
          archivable
        }
        counts
      }
      total
    }
  }
`;

const styles = StyleSheet.create({
  searchInput: {
    marginRight: 10,
    width: 200,
  },
  centerVertically: {
    display: 'flex',
    alignItems: 'center',
  },
  learnMoreText: {
    fontSize: '7px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  learnMoreButton: {
    padding: '0px 4px',
    marginLeft: '10px',
    height: '14px',
  },
  centerColumnHorizontally: {
    height: '70vh',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  emptyComponent: {
    paddingLeft: '30%',
    paddingRight: '30%',
  },
  headerDescription: {
    margin: '0px',
    color: NEAR_BLACK,
    ...FONT_PROXIMA_13,
  },
  darkGray: {
    color: GRAY_DARKER,
  },
  subDescription: {
    margin: '20px',
  },
  reviewDescription: {
    ...FONT_PROXIMA_11,
  },
  icon: {
    margin: '20px',
    width: '69px',
    height: '69px',
  },
});

const customEmptyComponent = (): JSX.Element => (
  <div className={css(styles.emptyComponent, styles.centerColumnHorizontally, styles.centerVertically)}>
    <BenchlingIcon className={css(styles.icon, styles.darkGray)} icon="benchling" />
    <div className={css(styles.headerDescription)}>Nothing here!</div>
  </div>
);

export function getCountsString(counts: {[key: string]: number}): string {
  /**
   * Return a string describing the counts and types of object in a collection
   *
   * @param counts - e.g. {"templates": 3, "procedures": 1, "form_definition": 0}
   * @returns a description of the counts - e.g. "3 entry templates, 1 recipe"
   */

  const countArray = Object.keys(counts)
    .filter((key) => counts[key] !== 0) // Remove any objects with a count of zero
    .filter((key) => TYPE_TO_LABEL.has(key)) // Be defensive if we're missing a label
    .map((key) => ({label: TYPE_TO_LABEL.get(key)!(), count: counts[key]}));
  return sortedBy(countArray, ['count', 'label'])
    .reverse() // Sort by count descending
    .map(({label, count}) => `${count} ${pluralize(count, label)}`)
    .join(', ');
}

function mapTemplateCollectionToRowValue(tc: TemplateCollection): Array<JSX.Element | string> {
  const row = [
    <div>{tc.archiveRecord ? `${tc.name} (${tc.archiveRecord.purpose})` : tc.name}</div>,
    tc.ownerJson.name,
    readableTime(tc.createdAt, {showTime: false}),
    readableTime(tc.modifiedAt, {showTime: false}),
    getCountsString(tc.counts),
  ];
  return row;
}

export function onTemplateCollectionArchive(
  collections: Array<ArchivableItem & TemplateCollection>,
  refetch: () => void
): void {
  const filteredCollections = collections.filter((collection) => !collection.archiveRecord);

  ArchiveModal.open({
    items: filteredCollections,
    onArchive: (
      templateCollectionIds: Array<string>,
      purpose: string,
      options: BulkArchiveFilesOptions
    ): void => {
      getArchiveAction(templateCollectionIds)(templateCollectionIds, purpose, options, refetch);
    },
  });
}

export function onTemplateCollectionUnarchive(
  collections: Array<ArchivableItem & TemplateCollection>,
  refetch: () => void
): void {
  const filteredCollections = collections.filter((collection) => collection.archiveRecord);
  const templateCollectionIds = filteredCollections.map((collection) => collection.id);
  getUnarchiveAction(templateCollectionIds)(templateCollectionIds, undefined, refetch);
}

export function TemplateCollectionsTable(): JSX.Element | null {
  const [searchQuery, setSearchQuery] = useState(EMPTY_SEARCH_QUERY);
  const [limitOffsetInput, setLimitOffsetInput] = useState<LimitOffsetInput>(DEFAULT_LIMIT_OFFSET_INPUT);
  const [archivePurposeFilters, setArchivePurposeFilters] = useState([NOT_ARCHIVED_PURPOSE_STRING]);

  function setOffset(offset: number): void {
    setLimitOffsetInput((oldLimitOffsetInput) => ({
      ...oldLimitOffsetInput,
      offset,
    }));
  }

  const result = useBenchlingQuery<TemplateCollectionsPageQuery, TemplateCollectionsPageQueryVariables>(
    TEMPLATE_COLLECTIONS_PAGE_QUERY,
    {
      variables: {
        limitOffsetInput,
        searchInput: searchQuery,
        archivePurposes: archivePurposeFilters,
      },
      fetchPolicy: 'network-only',
    }
  );

  if (result.type === QueryResultType.ERROR) {
    notifications.error('Error occured while loading template collections; try reloading the page.');
    return null;
  }
  const {data} = result;
  const total = data ? data.templateCollectionListing.total : 0;
  const collections = data ? data.templateCollectionListing.collections : [];

  const getItemUrl = (itemIndex: number): string => collections[itemIndex].url;

  const rows: Array<RowProps> =
    result.type === QueryResultType.SUCCESS && collections
      ? collections.map((tc, itemIndex) => {
          const row: RowProps = {
            id: tc.id,
            isArchived: isArchived(tc),
            isArchivable: Boolean(tc.permissions?.archivable),
            itemIndex,
            values: mapTemplateCollectionToRowValue(tc),
          };
          return row;
        })
      : [];

  const disabledFromOwnerChoices = getOwnerChoices().length === 0;
  const isDisabled = result.type === QueryResultType.PENDING || disabledFromOwnerChoices;

  const openCreateModal = (): void => {
    runModal(CreateTemplateCollectionModal, {});
  };

  return (
    <div>
      <EditableItemTable
        columnHeaders={COLUMN_HEADERS}
        customCreateButton={
          <div>
            <Tooltip
              title={
                disabledFromOwnerChoices
                  ? 'Please contact your administrator to create a template collection'
                  : 'New Collection'
              }
              placement={TooltipPlacement.BOTTOM}
              container="body"
            >
              <Button
                buttonStyle={ButtonStyle.PRIMARY}
                text="Create collection"
                size={ButtonSize.SMALL}
                onClick={openCreateModal}
                disabled={isDisabled}
              />
            </Tooltip>
          </div>
        }
        emptyMessage={customEmptyComponent()}
        isLoading={result.type === QueryResultType.PENDING}
        rows={rows}
        getEditUrl={getItemUrl}
        writable={true}
        onArchive={
          BenchlingConfig.ELN_ENABLE_TEMPLATE_ARCHIVING
            ? (itemIndex: number) => {
                onTemplateCollectionArchive([collections[itemIndex]], result.refetch);
              }
            : null
        }
        onUnarchive={
          BenchlingConfig.ELN_ENABLE_TEMPLATE_ARCHIVING
            ? (itemIndex: number) => {
                onTemplateCollectionUnarchive([collections[itemIndex]], result.refetch);
              }
            : null
        }
        leftSubheaderNode={
          <div>
            <div className="u-flex-display">
              <PlainSearchInput
                className={css(styles.searchInput)}
                borderRadius={BorderRadius.LARGE}
                placeholder="Search"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchQuery(e.target.value);
                  // Reset the offset when conducting a search
                  setOffset(0);
                }}
                onEscape={() => {
                  setSearchQuery(EMPTY_SEARCH_QUERY);
                  // Reset the offset when conducting a search
                  setOffset(0);
                }}
              />
              {BenchlingConfig.ELN_ENABLE_TEMPLATE_ARCHIVING && (
                <ArchivePurposeDropdown
                  itemIds={result.type === QueryResultType.PENDING ? [] : collections.map((tc) => tc.id)}
                  onChangePurposes={setArchivePurposeFilters}
                  selectedPurposes={archivePurposeFilters}
                />
              )}
            </div>
            <ResultsPager
              allowFastForwardAndBackward={true}
              isLoading={result.type === QueryResultType.PENDING}
              pagination={{...limitOffsetInput, total}}
              resultDescription="template collection"
              setOffset={setOffset}
            />
          </div>
        }
      />
      {total > limitOffsetInput.limit && (
        <ResultsPager
          allowFastForwardAndBackward={true}
          isLoading={result.type === QueryResultType.PENDING}
          pagination={{...limitOffsetInput, total}}
          resultDescription="template collection"
          setOffset={setOffset}
        />
      )}
    </div>
  );
}

export default function TemplateCollectionsPage(props: {}): JSX.Element {
  setWindowTitle('Template collections');

  return (
    <div className="u-flex-display u-vertical-flex u-height-full">
      <div className="u-flex-none">
        <TemplateBanner />
        <ContextualTemplateEditingInlineBanner />
      </div>
      <div className="u-flex-full-height">
        <SettingsPage title="My collections">
          <TemplateCollectionsTable {...props} />
        </SettingsPage>
      </div>
    </div>
  );
}
